import { create } from "zustand";
import { ResizableOutlineRef } from "./ResizableOutline";
import { createConditionWaiter } from "@/utils/conditionWaiter";
import { startBackgroundJobRunner } from "@/utils/backgroundJobRunner";

interface SimpleCanvasStore {
  isResizing: boolean;
  isPanning: boolean;
  isZooming: boolean;
  isDragging: boolean;
  isDragSelecting: boolean;
  isDrawing: boolean;
  setIsResizing: (isResizing: boolean) => void;
  setIsPanning: (isPanning: boolean) => void;
  setIsZooming: (isZooming: boolean) => void;
  setIsDragging: (isDragging: boolean) => void;
  setIsDragSelecting: (isDragSelecting: boolean) => void;
  setIsDrawing: (isDrawing: boolean) => void;

  isRealtimeInteractionActive: () => boolean;

  itemToResizableOutlineRef: Record<string, ResizableOutlineRef>;
  setItemToResizableOutlineRef: (
    itemId: string,
    ref: ResizableOutlineRef | null,
  ) => void;
}

export const useSimpleCanvasStore = create<SimpleCanvasStore>((set, get) => ({
  itemToResizableOutlineRef: {},
  setItemToResizableOutlineRef: (itemId, ref) => {
    const newItemToResizableOutlineRef = {
      ...get().itemToResizableOutlineRef,
    };
    if (ref === null) {
      delete newItemToResizableOutlineRef[itemId];
    } else {
      newItemToResizableOutlineRef[itemId] = ref;
    }
    set({
      itemToResizableOutlineRef: newItemToResizableOutlineRef,
    });
  },
  isResizing: false,
  setIsResizing: (isResizing: boolean) => set({ isResizing }),
  isPanning: false,
  setIsPanning: (isPanning: boolean) => set({ isPanning }),
  isZooming: false,
  setIsZooming: (isZooming: boolean) => set({ isZooming }),
  isDragging: false,
  setIsDragging: (isDragging: boolean) => set({ isDragging }),
  isDragSelecting: false,
  setIsDragSelecting: (isDragSelecting: boolean) => set({ isDragSelecting }),
  isDrawing: false,
  setIsDrawing: (isDrawing: boolean) => set({ isDrawing }),
  isRealtimeInteractionActive: () =>
    get().isResizing ||
    get().isPanning ||
    get().isZooming ||
    get().isDragging ||
    get().isDragSelecting ||
    get().isDrawing,
}));

// Wait for canvas to be idle (no interaction) for at least 100ms before considering it idle
const waitUntilCanvasIdle = createConditionWaiter({
  conditionSubscriber: (listener) =>
    useSimpleCanvasStore.subscribe((state) => {
      listener(!state.isRealtimeInteractionActive());
    }),
  minTrueDurationMs: 100,
}).waitUntilConditionTrue;

const canvasAwareIdleJobRunner = startBackgroundJobRunner({
  waitUntilEnvironmentIdle: waitUntilCanvasIdle,
});

export const scheduleCanvasIdleTask = <T>(
  task: () => T,
  timeoutMs?: number,
): Promise<T> =>
  canvasAwareIdleJobRunner.enqueue({
    run: task,
    idleTimeout: timeoutMs, // Max amount of time to wait for the canvas to be idle before running the task
  });

// const canvasIdleTester = (id: number = 0) => {
//   scheduleCanvasIdleTask(() => {
//     console.log("Idle Task", id);
//   });
//   setTimeout(() => {
//     canvasIdleTester(id + 1);
//   }, 1000);
// }
// canvasIdleTester();
